import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { CurrencyFieldDefinition } from '@fsx/fsx-shared';
import { formatValue } from '../../helpers/input-formatter.helper';
import { parseValue } from '../../helpers/input-parser.helper';
import { sanitizeValue } from '../../helpers/input-sanitizer.helper';
import { FormControlWithModel } from '../../models/form-control.model';
import { FSXFormControlService } from '../../services';

@Component({
  selector: 'fsx-currency-component',
  templateUrl: './currency.component.html',
  styleUrls: ['./currency.component.scss'],
})
export class FsxCurrencyComponent implements OnInit {
  @Input() fieldDefinition!: CurrencyFieldDefinition;
  @Input() initialValue!: string | number | null;
  @Input() caption!: string;
  @Input() helpText!: string;
  @Input() hint!: string;
  @Input() fieldType!: string;
  @Input() width!: string;
  @Output() formControlEmitter = new EventEmitter<
    FormControlWithModel<CurrencyFieldDefinition>
  >();

  public formControl!: FormControlWithModel<CurrencyFieldDefinition>;
  public parseError!: string | null;
  public showPlaceholder = true;

  constructor(private readonly fsxFormControlService: FSXFormControlService) {}

  ngOnInit(): void {
    this.formControl = this.fsxFormControlService.createFormControl(
      this.fieldDefinition,
      this.fieldType,
      this.initialValue || this.initialValue === 0
        ? this.initialValue
        : this.fieldDefinition.defaultValue,
    );
    this.formControlEmitter.emit(this.formControl);

    this.formatValue();
  }

  public getErrorMessage(): string {
    return Object.values({ ...this.formControl.errors })[0];
  }

  public formatValue(): void {
    const value = String(this.formControl.value ?? '').trim();
    const fieldType = this.fieldType;
    const allowDecimals = this.formControl.fieldDefinition.allowDecimals;

    if (value !== null && value.length) {
      const sanitizedValue = sanitizeValue(value, fieldType);
      const parsedValue = parseValue(sanitizedValue, fieldType);

      if (typeof parsedValue !== 'number') {
        this.parseError = 'Invalid currency value';
        this.formControl.setValue(value);
        return;
      }

      this.parseError = null;
      const formattedValue = formatValue(parsedValue, fieldType, {
        allowDecimals,
      });
      this.formControl.formattedValue = parseFloat(String(formattedValue));
      this.formControl.setValue(formattedValue);
    } else {
      this.formControl.setValue(null);
    }
  }

  public togglePlaceholder(value: boolean): void {
    this.showPlaceholder = value;
  }

  public get inputPlaceholder(): string {
    return this.formControl.value === null && this.showPlaceholder
      ? this.hint || '$ 0.00'
      : '';
  }

  public get invalidInput(): boolean {
    return this.formControl.invalid && this.formControl.touched;
  }

  public get showingPlaceholder(): boolean {
    return this.formControl.value === null && this.showPlaceholder;
  }

  public validate(): void {
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    this.formControl.updateValueAndValidity({ emitEvent: false });
  }
}
